import { useCallback, useEffect, useRef, useState } from 'react'
import { AnchorType, Source } from 'types'
import { LoadingScreen } from 'screens/BrandPlan/ChangeStrategyModal/LoadingScreen/LoadingScreen'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { useCreateMediaObjectsWithProgress } from 'hooks/queries/mediaObjects/useCreateMediaObjects'

interface UploadingAnchorContentFileProps {
  anchorType: AnchorType
  signal?: AbortSignal
  file: File
  onSourceCreated: (source: Source) => Promise<any> | void
}

export const UploadCampaignSourceModal = ({ anchorType, file, onSourceCreated }: UploadingAnchorContentFileProps) => {
  const { closeModal } = useModals()
  const [progress, setProgress] = useState(0)
  const abortControllerRef = useRef(new AbortController())
  const { mutateAsync: createMediaObjectsWithProgress } = useCreateMediaObjectsWithProgress({
    onProgress: setProgress,
  })

  const { mutateAsync: createSource } = useCreateSource()

  const handleBack = () => {
    abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()
    closeModal()
  }

  const triggerUpload = useCallback(async () => {
    const mediaObjects = await createMediaObjectsWithProgress([{ file, filename: file.name }])
    const result = await createSource({ media_object_id: mediaObjects[0].id, type: 'SourceMaterialsMediaObject' })
    await onSourceCreated(result.source)

    closeModal()
  }, [createMediaObjectsWithProgress, file, createSource, onSourceCreated, closeModal])

  useEffect(() => {
    triggerUpload()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  let url =
    'https://res.cloudinary.com/almanac/image/upload/v1756851647/blaze_assets/virtual_marketer/strategies/DocPreview_yivwe3.png'
  if (anchorType === 'audio') {
    url =
      'https://res.cloudinary.com/almanac/image/upload/v1756851647/blaze_assets/virtual_marketer/strategies/AudioPreview_imrx2w.png'
  } else if (anchorType === 'video') {
    url =
      'https://res.cloudinary.com/almanac/image/upload/v1755891620/blaze_assets/virtual_marketer/strategies/Thumb-3_bni7rb.png'
  }

  return (
    <Modal.Root size="sm" onPressOutside={closeModal} aria-label="Uploading Campaign Source">
      <Modal.Header
        title={
          <>
            <Modal.BackButton onPress={handleBack} />
            Uploading Campaign Source
          </>
        }
      />
      <Modal.Content>
        <LoadingScreen
          anchorType={anchorType}
          header="Reviewing your content 👀"
          realProgress={Math.floor(progress * 10) / 10}
          labels={['Uploading', `Analyzing your ${anchorType}`, 'Reviewing content']}
          previewUrl={url}
        />
      </Modal.Content>
    </Modal.Root>
  )
}
